import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { PieChart, Pie, Legend, Sector,LabelList } from 'recharts';

// Chart Components for the Dashboard
export const DashboardCharts = ({ students }) => {
  // Process data for charts
  const [chartData, setChartData] = useState({
    branchData: [],
    courseTypeData: [],
    sourceData: []
  });

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#a64d79', '#674ea7', '#3c78d8', '#6aa84f'];

  useEffect(() => {
    if (students && students.length > 0) {
      // Process branch data
      const branchCounts = {};
      students.forEach(student => {
        const branch = student.branch || 'Unknown';
        branchCounts[branch] = (branchCounts[branch] || 0) + 1;
      });
      
      const branchData = Object.keys(branchCounts).map(branch => ({
        name: branch,
        count: branchCounts[branch]
      })).sort((a, b) => b.count - a.count);

      // Process course type data
      const courseTypeCounts = {};
      students.forEach(student => {
        const courseType = student.courseType || 'Unknown';
        courseTypeCounts[courseType] = (courseTypeCounts[courseType] || 0) + 1;
      });
      
      const courseTypeData = Object.keys(courseTypeCounts).map(courseType => ({
        name: courseType,
        count: courseTypeCounts[courseType]
      })).sort((a, b) => b.count - a.count);

      // Process source data
      const sourceCounts = {};
      students.forEach(student => {
        const source = student.source || 'Unknown';
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;
      });
      
      const sourceData = Object.keys(sourceCounts).map(source => ({
        name: source,
        count: sourceCounts[source]
      })).sort((a, b) => b.count - a.count);

      setChartData({
        branchData,
        courseTypeData,
        sourceData
      });
    }
  }, [students]);

  return (
    <div className="dashboard1-charts">
      <h2 className="charts1-title">Enquiry Analytics</h2>
      <div className="charts1-container">
        <div className="chart1-card">
          <h3>Enquiry by Branch</h3>
          <BranchBarChart data={chartData.branchData} colors={COLORS} />
        </div>
        <div className="chart1-card">
          <h3>Enquiry by Course Type</h3>
          <CourseTypeGaugeChart data={chartData.courseTypeData} colors={COLORS} />
        </div>
        <div className="chart1-card">
          <h3>Enquiry by Source</h3>
          <SourceVerticalBarChart data={chartData.sourceData} colors={COLORS} />
        </div>
      </div>
    </div>
  );
};

// Horizontal Bar Chart for Branch data
const BranchBarChart = ({ data, colors }) => {
  if (!data || data.length === 0) return <div className="no-data">No data available</div>;
  
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        layout="vertical"
        data={data.slice(0, 8)} // Show top 8 branches
        margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" />
        <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
        <Tooltip formatter={(value) => [`${value} students`, 'Registrations']} />
        <Bar dataKey="count" name="Registrations">
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
            <LabelList dataKey="count" position="right" formatter={(value) => `${value}students`} />

        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// Gauge Chart (using Pie Chart) for Course Type
const CourseTypeGaugeChart = ({ data, colors }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  if (!data || data.length === 0) return <div className="no-data">No data available</div>;

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  
    return (
      <g>
        <text x={cx} y={cy - 20} dy={8} textAnchor="middle" fill={fill} fontSize={16}>
          {payload.name}
        </text>
        <text x={cx} y={cy + 10} dy={8} textAnchor="middle" fill="#333" fontSize={14}>
          {value} students
        </text>
        <text x={cx} y={cy + 30} dy={8} textAnchor="middle" fill="#999" fontSize={12}>
          {`(${(percent * 100).toFixed(2)}%)`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
      </g>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          fill="#8884d8"
          dataKey="count"
          onMouseEnter={onPieEnter}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
        </Pie>
        <Legend verticalAlign="bottom" height={36} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Vertical Bar Chart for Source data
const SourceVerticalBarChart = ({ data, colors }) => {
  if (!data || data.length === 0) return <div className="no-data">No data available</div>;
  
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data.slice(0, 8)} // Show top 8 sources
        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: 12 }}
          interval={0}
          angle={-45}
          textAnchor="end"
          height={70}
        />
        <YAxis />
        <Tooltip formatter={(value) => [`${value} students`, 'Registrations']} />
        <Bar dataKey="count" name="Registrations">
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
            <LabelList
    dataKey="count"
    position="top"
    formatter={(value) => `${value}students`}
    style={{ fontSize: 12 }}
  />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};